import dayjs from "dayjs";
import { FC, useEffect, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  Column,
  FormController,
  MuiBox,
  MuiDateFieldFormController,
  MuiGrid,
  MuiLink,
  MuiNotesToolTip,
  MuiOptionMenu,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
  RowProps,
  StatusBadge,
  TableHeaderSearchIcon,
} from "../../../../../../../../../../components";
import MuiEllipseLabel from "../../../../../../../../../../components/ellipseLabel/MuiEllipseLabel";
import {
  DATE_FORMAT,
  EMPTY_VALUE_DASH,
  MAX_LENGTH_50,
  PAGE_IDS,
} from "../../../../../../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../../../../../../helpers/constants/countryDateFormat";
import { LabelValue } from "../../../../../../../../../../helpers/types/common";
import {
  convertToUserFormat,
  filterListByFilterKeyValue,
  getUniqueFilters,
  getValueOrBlank,
  isNotEmpty,
} from "../../../../../../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../../../hooks/useSiteCore";
import { siteCoreContentLoadingSelector } from "../../../../../../../../../../redux/slices/appState/appStateSelector";
import {
  GoalSelectionResultDropdownFilterKey,
  GoalSelectionResultFormKey,
  GoalSelectionResultRow,
} from "../../modelsAndSchemas/Models";
import { goalSelectionSiteCoreKeys } from "../../SiteCoreKeys";
import { GoalSelectionResultWrapper } from "./GoalSelectionResultStyle";

interface GoalSelectionResultProps {
  rows: GoalSelectionResultRow[];
  onViewClick?: (id: string) => void;
  onEditClick?: (id: string) => void;
  onDeleteClick?: (id: string) => void;
  isViewOnly?: boolean;
}

const pageId = PAGE_IDS.CREATE_PROGRAM_GOAL_SELECTION_RESULT;

export const GoalSelectionResult: FC<GoalSelectionResultProps> = ({
  rows,
  onViewClick,
  onDeleteClick,
  onEditClick,
  isViewOnly,
}) => {
  const { commonARIALabel } = useNavigationSiteCore();
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);
  const {
    useFetchLabel,
    commonLabels,
    fetchValueByKeyOfDropdown,
    fetchKeyByValueOfDropdown,
  } = useSiteCore();
  const tableRef = useRef<{
    resetPagination: (shouldResetRowsPerPage?: boolean) => void;
  }>(null);
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );
  const [filteredResults, setFilteredResults] = useState([...rows]);
  const [uniqueFiltersOptionObj, setUniqueFiltersOptionObj] = useState<{
    [K in GoalSelectionResultDropdownFilterKey]: LabelValue[];
  }>({
    [GoalSelectionResultFormKey.status]: [],
  });

  const { control, getValues, reset } = useForm({
    mode: "all",
    defaultValues: {
      [GoalSelectionResultFormKey.goalName]: "",
      [GoalSelectionResultFormKey.goalTheme]: "",
      [GoalSelectionResultFormKey.goalDescription]: "",
      [GoalSelectionResultFormKey.status]: "",
      [GoalSelectionResultFormKey.dueDate]: "",
      [GoalSelectionResultFormKey.completionDate]: "",
    },
  });
  const tableColumnFilterWatch = useWatch({ control });

  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  const {
    goalSelectionTitle,
    goalNameTableColumn,
    goalThemeTableColumn,
    goalDescriptionTableColumn,
    goalStatusTableColumn,
    goalDueDateTableColumn,
    goalCompletionDateTableColumn,
    notesTableColumn,
    goalStatusList,
    actionTableColumn,
    noGoalsAvailableMessage,
  } = goalSelectionSiteCoreKeys;
  const getLabel = useFetchLabel(goalSelectionTitle);

  const getValueByKeyOfDropdown = (listKey: string, key: string) =>
    fetchValueByKeyOfDropdown(goalSelectionTitle, listKey, key);

  useEffect(() => {
    if (isNotEmpty(rows)) {
      const filterGoalDueDate = tableColumnFilterWatch.dueDate
        ? dayjs(tableColumnFilterWatch.dueDate, userCountryDateFormat).format(
            DATE_FORMAT.YYYY_MM_DD_HYPHEN,
          )
        : "";
      const filterGoalCompletionDate = tableColumnFilterWatch.completionDate
        ? dayjs(
            tableColumnFilterWatch.completionDate,
            userCountryDateFormat,
          ).format(DATE_FORMAT.YYYY_MM_DD_HYPHEN)
        : "";
      const filterStatusKey = tableColumnFilterWatch.status
        ? fetchKeyByValueOfDropdown(
            goalSelectionTitle,
            goalStatusList,
            tableColumnFilterWatch.status,
          )
        : "";
      
      const filterList = filterListByFilterKeyValue(rows, {
        ...tableColumnFilterWatch,
        status: filterStatusKey,
        dueDate: filterGoalDueDate,
        completionDate: filterGoalCompletionDate,
      }) as GoalSelectionResultRow[];
      setFilteredResults([...filterList]);
      resetPagination();
    }
  }, [tableColumnFilterWatch]);

  useEffect(() => {
    if (!siteCoreLoading) {
      const mappedResults = rows.map((item: GoalSelectionResultRow) => {
        return {
          ...item,
          statusLabelValue: getValueByKeyOfDropdown(
            goalStatusList,
            item.status,
          ),
        };
      });
      const uniqueGoalStatuses = getUniqueFilters(
        mappedResults,
        "statusLabelValue",
      );
      setUniqueFiltersOptionObj({
        [GoalSelectionResultFormKey.status]: uniqueGoalStatuses,
      });

      setFilteredResults([...mappedResults]);
      reset();
      resetPagination();
    }
  }, [rows, siteCoreLoading]);

  const columns: Column[] = [
    {
      id: GoalSelectionResultFormKey.goalName,
      isSortVisible: true,
      label: getLabel(goalNameTableColumn),
      isEmptyValue: true,
      render: (row: RowProps) => (
        <MuiLink onClick={() => onViewClick?.(row?.id)}>
          <MuiEllipseLabel
            labelTypoClassName="Link goal-name-link"
            text={row.name}
            labelTypoVariant="h3"
            placement="bottom-start"
            pointerCursor
          />
        </MuiLink>
      ),
    },
    {
      id: GoalSelectionResultFormKey.goalTheme,
      isSortVisible: true,
      label: getLabel(goalThemeTableColumn),
      isEmptyValue: true,
    },
    {
      id: GoalSelectionResultFormKey.goalDescription,
      isSortVisible: true,
      label: getLabel(goalDescriptionTableColumn),
      isEmptyValue: true,
    },
    {
      id: GoalSelectionResultFormKey.dueDate,
      isSortVisible: true,
      label: getLabel(goalDueDateTableColumn),
      render: (row: RowProps) => (
        <MuiBox>
          <MuiTypography variant="h3">
            {isNotEmpty(row.dueDate)
              ? convertToUserFormat(row.dueDate, userCountryDateFormat)
              : EMPTY_VALUE_DASH}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: GoalSelectionResultFormKey.status,
      isSortVisible: true,
      isEmptyValue: true,
      label: getLabel(goalStatusTableColumn),
      minWidth: 220,
      render: (row: RowProps) => (
        <StatusBadge
          label={getValueByKeyOfDropdown(goalStatusList, row.status)}
          status={row.status}
        />
      ),
    },
    {
      id: GoalSelectionResultFormKey.completionDate,
      isSortVisible: true,
      label: getLabel(goalCompletionDateTableColumn),
      render: (row: RowProps) => (
        <MuiBox>
          <MuiTypography variant="h3">
            {isNotEmpty(row.completionDate)
              ? convertToUserFormat(row.completionDate, userCountryDateFormat)
              : EMPTY_VALUE_DASH}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: GoalSelectionResultFormKey.goalNotes,
      label: getLabel(notesTableColumn),
      isSortVisible: false,
      render: (row: any) => (
        <MuiBox>
          <MuiNotesToolTip message={row?.notes} />
        </MuiBox>
      ),
    },
    {
      id: "action",
      label: getLabel(actionTableColumn),
      render: (row: RowProps) => {
        const actions = [
          {
            onClick: () => onViewClick?.(row?.id),
            dataTestId: "view-goal-action",
            label: commonLabels.view,
          },
        ];
        if (!isViewOnly) {
          actions.push(
            {
              onClick: () => onEditClick?.(row?.id),
              dataTestId: "edit-goal-action",
              label: commonLabels.edit,
            },
            {
              onClick: () => onDeleteClick?.(row?.id),
              dataTestId: "delete-goal-action",
              label: commonLabels.delete,
            },
          );
        }

        return <MuiOptionMenu menuItems={actions} />;
      },
    },
  ];

  const renderFilterTextField = (name: string) => {
    return (
      <FormController key={name} control={control} name={name}>
        <MuiTextField
          key={name}
          id={`${pageId}-${name}`}
          aria-label={commonLabels.search}
          startAdornment={<TableHeaderSearchIcon />}
          data-testid="company-input"
          showCloseIcon={true}
          placeholder={commonLabels.search}
          maxLength={MAX_LENGTH_50}
          requiredARIALabel={commonARIALabel.required}
          shouldTrimSpaces={false}
        />
      </FormController>
    );
  };

  const renderFilterDropdown = (name: string) => {
    const options = uniqueFiltersOptionObj[name];
    return (
      <FormController key={name} control={control} name={name}>
        <MuiSelect
          id={`${pageId}-${name}`}
          value=""
          label=""
          options={options}
          visibleDefaultSelectOption={true}
          requiredARIALabel={commonARIALabel.required}
          placeholder={commonLabels.selectPlaceHolder}
        />
      </FormController>
    );
  };

  const tableHeaderForm = [
    renderFilterTextField(GoalSelectionResultFormKey.goalName),
    renderFilterTextField(GoalSelectionResultFormKey.goalTheme),
    renderFilterTextField(GoalSelectionResultFormKey.goalDescription),
    <MuiDateFieldFormController
      control={control}
      key={`${pageId}-${GoalSelectionResultFormKey.dueDate}`}
      id={`${pageId}-${GoalSelectionResultFormKey.dueDate}`}
      name={GoalSelectionResultFormKey.dueDate}
      valueFormat={userCountryDateFormat}
      format={userCountryDateFormat}
      requiredARIALabel={commonARIALabel.required}
      selectDateARIALabel={commonARIALabel.selectDate}
      clearable
      hideCalendarIconOnValue={isNotEmpty(
        getValues(GoalSelectionResultFormKey.dueDate),
      )}
    />,
    renderFilterDropdown(GoalSelectionResultFormKey.status),
    <MuiDateFieldFormController
      control={control}
      id={`${pageId}-${GoalSelectionResultFormKey.completionDate}`}
      key={`${pageId}-${GoalSelectionResultFormKey.completionDate}`}
      name={GoalSelectionResultFormKey.completionDate}
      valueFormat={userCountryDateFormat}
      format={userCountryDateFormat}
      requiredARIALabel={commonARIALabel.required}
      selectDateARIALabel={commonARIALabel.selectDate}
      clearable
      hideCalendarIconOnValue={isNotEmpty(
        getValues(GoalSelectionResultFormKey.completionDate),
      )}
    />,
    <></>,
    <></>,
  ];

  return (
    <GoalSelectionResultWrapper>
      <MuiGrid container data-testid="goal-selection-result">
        <MuiGrid container className="width-100">
          <MuiGrid size={{ xs: 12 }}>
            <MuiTable
              ref={tableRef}
              pageId={pageId}
              tableHeaderForm={tableHeaderForm}
              columns={columns}
              tableResults={[...filteredResults]}
              noDataInApi={rows.length === 0}
              isLastColumnSticky
              isShowDashIfValueEmpty
              noResultMessage={commonLabels.noResult}
              emptyMessage={getLabel(noGoalsAvailableMessage)}
              priorityResults={[]}
            />
          </MuiGrid>
        </MuiGrid>
      </MuiGrid>
    </GoalSelectionResultWrapper>
  );
};
